import React from 'react';
import { ACTIVE_GREEN, ACTIVE_GREEN_BG, getStatusChipSx, INACTIVE_STATUS_BG, INACTIVE_STATUS_COLOR } from '../../../constants/statusColors';
import {
  Grid,
  Card,
  CardContent,
  Paper,
  Box,
  Typography,
  Chip,
} from '@mui/material';
import {
  BadgeOutlined as IdIcon,
  PublicOutlined as NationalityIcon,
  CakeOutlined as BirthdayIcon,
  WcOutlined as GenderIcon,
  EmailOutlined as EmailIcon,
  PhoneOutlined as PhoneIcon,
  HomeOutlined as AddressIcon,
  BusinessOutlined as CompanyIcon,
  WorkOutlined as PositionIcon,
  WorkOutlined,
  CalendarTodayOutlined as DateIcon,
  DescriptionOutlined as DocumentIcon,
  GroupsOutlined as CommitteesIcon,
  AssignmentOutlined as PassportIcon,
} from '@mui/icons-material';

const OverviewTab = ({ director }) => {
  const formatDate = (date) => {
    if (!date) return 'N/A';
    return new Date(date).toLocaleDateString('en-GB');
  };

  // Calculate stats
  const totalDocuments = director.documents?.length || 0;
  const validDocuments = director.documents?.filter(doc => {
    const daysUntil = Math.ceil((new Date(doc.expiryDate) - new Date()) / (1000 * 60 * 60 * 24));
    return daysUntil > 30;
  }).length || 0;
  const expiringDocuments = director.documents?.filter(doc => {
    const daysUntil = Math.ceil((new Date(doc.expiryDate) - new Date()) / (1000 * 60 * 60 * 24));
    return daysUntil >= 0 && daysUntil <= 30;
  }).length || 0;
  const totalCommittees = director.committeeCount ?? director.committees?.length ?? 0;

  const StatCard = ({ icon: Icon, label, value, color }) => (
    <Card sx={{ height: '100%', borderRadius: '12px' }}>
      <CardContent>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5, mb: 1 }}>
          <Box
            sx={{
              width: 40,
              height: 40,
              borderRadius: '8px',
              bgcolor: `${color}15`,
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              color: color,
            }}
          >
            <Icon sx={{ fontSize: 24 }} />
          </Box>
        </Box>
        <Typography variant="h4" fontWeight={600} color={color}>
          {value}
        </Typography>
        <Typography variant="body2" color="textSecondary">
          {label}
        </Typography>
      </CardContent>
    </Card>
  );

  const InfoItem = ({ icon: Icon, label, value, iconBg, iconColor }) => (
    <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, mb: 2.5 }}>
      <Box
        sx={{
          width: 48,
          height: 48,
          borderRadius: '12px',
          bgcolor: iconBg,
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          flexShrink: 0,
        }}
      >
        <Icon sx={{ fontSize: 24, color: iconColor }} />
      </Box>
      <Box sx={{ flex: 1 }}>
        <Typography variant="caption" color="textSecondary" display="block">
          {label}
        </Typography>
        <Typography variant="body1" fontWeight={500}>
          {value}
        </Typography>
      </Box>
    </Box>
  );

  return (
    <Box>
      {/* Stat Cards */}
      <Grid container spacing={2} sx={{ mb: 3 }}>
        <Grid item xs={12} sm={6} md={3}>
          <StatCard
            icon={DocumentIcon}
            label="Total Documents"
            value={totalDocuments}
            color="#1976D2"
          />
        </Grid>
        <Grid item xs={12} sm={6} md={3}>
          <StatCard
            icon={DocumentIcon}
            label="Valid Documents"
            value={validDocuments}
            color={ACTIVE_GREEN}
          />
        </Grid>
        <Grid item xs={12} sm={6} md={3}>
          <StatCard
            icon={DocumentIcon}
            label="Expiring Soon"
            value={expiringDocuments}
            color="#E65100"
          />
        </Grid>
        <Grid item xs={12} sm={6} md={3}>
          <StatCard
            icon={CommitteesIcon}
            label="Committees"
            value={totalCommittees}
            color="#7B1FA2"
          />
        </Grid>
      </Grid>

      {/* Main Information Sections */}
      <Grid container spacing={3}>
        {/* Personal Information */}
        <Grid item xs={12} md={6}>
          <Paper sx={{ p: 3, height: '100%', borderRadius: '12px' }}>
            <Typography variant="h6" gutterBottom sx={{ mb: 3 }}>
              Personal Information
            </Typography>

            <InfoItem
              icon={IdIcon}
              label="Emirates ID"
              value={director.emiratesId || 'N/A'}
              iconBg="#F5F5F5"
              iconColor="#616161"
            />

            <InfoItem
              icon={PassportIcon}
              label="Passport Number"
              value={director.passportNumber || 'N/A'}
              iconBg="#F5F5F5"
              iconColor="#616161"
            />

            <InfoItem
              icon={NationalityIcon}
              label="Nationality"
              value={director.nationality || 'N/A'}
              iconBg="#F5F5F5"
              iconColor="#616161"
            />

            <InfoItem
              icon={BirthdayIcon}
              label="Date of Birth"
              value={formatDate(director.dateOfBirth)}
              iconBg="#F5F5F5"
              iconColor="#616161"
            />

            <InfoItem
              icon={GenderIcon}
              label="Gender"
              value={director.gender || 'N/A'}
              iconBg="#F5F5F5"
              iconColor="#616161"
            />
          </Paper>
        </Grid>

        {/* Contact Information */}
        <Grid item xs={12} md={6}>
          <Paper sx={{ p: 3, height: '100%', borderRadius: '12px' }}>
            <Typography variant="h6" gutterBottom sx={{ mb: 3 }}>
              Contact Information
            </Typography>

            <InfoItem
              icon={EmailIcon}
              label="Email"
              value={director.email || 'N/A'}
              iconBg="#F5F5F5"
              iconColor="#616161"
            />

            <InfoItem
              icon={PhoneIcon}
              label="Mobile Number"
              value={director.mobileNumber || 'N/A'}
              iconBg="#F5F5F5"
              iconColor="#616161"
            />

            <InfoItem
              icon={AddressIcon}
              label="Address"
              value={director.address || 'Not provided'}
              iconBg="#F5F5F5"
              iconColor="#616161"
            />
          </Paper>
        </Grid>

        {/* Company Assignment */}
        <Grid item xs={12} md={6}>
          <Paper sx={{ p: 3, height: '100%', borderRadius: '12px' }}>
            <Typography variant="h6" gutterBottom sx={{ mb: 3 }}>
              Company Assignment
            </Typography>

            <InfoItem
              icon={CompanyIcon}
              label="Company"
              value={director.companyName || 'N/A'}
              iconBg="#F5F5F5"
              iconColor="#616161"
            />

            <InfoItem
              icon={PositionIcon}
              label="Position/Title"
              value={director.position || 'N/A'}
              iconBg="#F5F5F5"
              iconColor="#616161"
            />

            <InfoItem
              icon={DateIcon}
              label="Appointment Date"
              value={formatDate(director.appointmentDate)}
              iconBg="#F5F5F5"
              iconColor="#616161"
            />

            <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
              <Box
                sx={{
                  width: 48,
                  height: 48,
                  borderRadius: '12px',
                  bgcolor: director.status === 'ACTIVE' ? ACTIVE_GREEN_BG : INACTIVE_STATUS_BG,
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  flexShrink: 0,
                }}
              >
                <WorkOutlined sx={{ fontSize: 24, color: director.status === 'ACTIVE' ? ACTIVE_GREEN : INACTIVE_STATUS_COLOR }} />
              </Box>
              <Box sx={{ flex: 1 }}>
                <Typography variant="caption" color="textSecondary" display="block">
                  Status
                </Typography>
                <Chip
                  label={director.status}
                  sx={{
                    ...getStatusChipSx(director.status),
                    fontWeight: 600,
                    fontSize: '0.75rem',
                    height: 24,
                    borderRadius: '12px',
                    textTransform: 'uppercase',
                    letterSpacing: '0.5px',
                    mt: 0.5,
                  }}
                  size="small"
                />
              </Box>
            </Box>
          </Paper>
        </Grid>

        {/* Document Expiry Status */}
        <Grid item xs={12} md={6}>
          <Paper sx={{ p: 3, height: '100%', borderRadius: '12px' }}>
            <Typography variant="h6" gutterBottom sx={{ mb: 3 }}>
              Document Expiry Status
            </Typography>

            {director.documents && director.documents.length > 0 ? (
              <Box>
                {director.documents.map((doc) => (
                  <Box key={doc.id} sx={{ display: 'flex', alignItems: 'center', gap: 2, mb: 2.5 }}>
                    <Box
                      sx={{
                        width: 48,
                        height: 48,
                        borderRadius: '12px',
                        bgcolor: '#F5F5F5',
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                        flexShrink: 0,
                      }}
                    >
                      <DocumentIcon sx={{ fontSize: 24, color: '#616161' }} />
                    </Box>
                    <Box sx={{ flex: 1 }}>
                      <Typography variant="body2" fontWeight={500}>
                        {doc.type}
                      </Typography>
                      <Typography variant="caption" color="textSecondary">
                        Expires: {formatDate(doc.expiryDate)}
                      </Typography>
                    </Box>
                    <Chip
                      label={getExpiryStatus(doc.expiryDate)}
                      sx={{
                        bgcolor: getExpiryBgColor(doc.expiryDate),
                        color: getExpiryTextColor(doc.expiryDate),
                        fontWeight: 600,
                        fontSize: '0.75rem',
                        height: 24,
                        borderRadius: '12px',
                        textTransform: 'uppercase',
                        letterSpacing: '0.5px',
                      }}
                      size="small"
                    />
                  </Box>
                ))}
              </Box>
            ) : (
              <Typography variant="body2" color="textSecondary">
                No documents uploaded
              </Typography>
            )}
          </Paper>
        </Grid>
      </Grid>
    </Box>
  );
};

const getExpiryStatus = (expiryDate) => {
  if (!expiryDate) return 'No Date';
  const daysUntil = Math.ceil((new Date(expiryDate) - new Date()) / (1000 * 60 * 60 * 24));
  if (daysUntil < 0) return 'Expired';
  if (daysUntil <= 30) return 'Expiring Soon';
  return 'Valid';
};

const getExpiryBgColor = (expiryDate) => {
  if (!expiryDate) return '#F5F5F5';
  const daysUntil = Math.ceil((new Date(expiryDate) - new Date()) / (1000 * 60 * 60 * 24));
  if (daysUntil < 0) return '#FFEBEE'; // Red
  if (daysUntil <= 30) return '#FFF3E0'; // Orange
  return ACTIVE_GREEN_BG; // Green
};

const getExpiryTextColor = (expiryDate) => {
  if (!expiryDate) return '#757575';
  const daysUntil = Math.ceil((new Date(expiryDate) - new Date()) / (1000 * 60 * 60 * 24));
  if (daysUntil < 0) return '#C62828'; // Red
  if (daysUntil <= 30) return '#E65100'; // Orange
  return ACTIVE_GREEN; // Green
};

export default OverviewTab;
